
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Company, CompanyTheme } from '@/types/company';
import { updateCompanyTheme } from '@/lib/supabase/company';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { isValidUUID } from '@/lib/utils';
import { 
  isDevelopmentEnvironment, 
  getFromLocalStorage, 
  saveToLocalStorage 
} from '@/lib/supabase/development-mode';

// UUID predeterminado para desarrollo
const FALLBACK_UUID = "00000000-0000-0000-0000-000000000000";

// Función para actualizar el tema en modo desarrollo
const updateMockCompanyTheme = (companyId: string, theme: CompanyTheme) => {
  const authData = getFromLocalStorage('auth', {});
  const companyData = authData.company || {};
  
  if (companyData && companyData.id === companyId) {
    const updatedCompany = {
      ...companyData,
      theme
    };
    saveToLocalStorage('auth', {
      ...authData,
      company: updatedCompany
    });
  }
  return theme;
};

// Función para obtener el tema en modo desarrollo
const getMockCompanyTheme = (companyId: string): CompanyTheme => {
  const authData = getFromLocalStorage('auth', {});
  const companyData = authData.company || {};
  
  if (companyData && companyData.id === companyId && companyData.theme) {
    return companyData.theme;
  }
  return {
    primaryColor: '#1E8449',
    secondaryColor: '#F1C40F',
    accentColor: '#2C3E50'
  };
};

// Esquema de validación
const formSchema = z.object({
  primaryColor: z.string().min(4, {
    message: 'Por favor ingrese un color primario válido',
  }),
  secondaryColor: z.string().min(4, {
    message: 'Por favor ingrese un color secundario válido',
  }),
  accentColor: z.string().min(4, {
    message: 'Por favor ingrese un color de acento válido',
  }),
  logoUrl: z.string().nullable().optional(),
  customCss: z.string().optional(),
  fontPrimary: z.string().nullable().optional(),
  fontSecondary: z.string().nullable().optional(),
});

export type ThemeFormValues = z.infer<typeof formSchema>;

export function useBrandingForm(company: Company) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser } = useAuth();
  
  const form = useForm<ThemeFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      primaryColor: company.theme?.primaryColor || '#1E8449',
      secondaryColor: company.theme?.secondaryColor || '#F1C40F',
      accentColor: company.theme?.accentColor || '#2C3E50',
      logoUrl: company.theme?.logoUrl || null,
      customCss: company.theme?.customCss || '',
      fontPrimary: company.theme?.fontPrimary || null,
      fontSecondary: company.theme?.fontSecondary || null,
    },
  });
  
  const onSubmit = async (data: ThemeFormValues) => {
    setIsSubmitting(true);
    
    try {
      if (!isValidUUID(company.id)) {
        throw new Error('ID de empresa inválido');
      }
      
      // En modo desarrollo, actualizar el localStorage
      if (isDevelopmentEnvironment) {
        const theme = updateMockCompanyTheme(company.id, data as CompanyTheme);
        toast.success('Tema actualizado con éxito');
        return theme;
      } 
      
      // En producción, actualizar en Supabase
      const updatedTheme = await updateCompanyTheme(company.id, {
        primaryColor: data.primaryColor,
        secondaryColor: data.secondaryColor,
        accentColor: data.accentColor,
        logoUrl: data.logoUrl || undefined,
        customCss: data.customCss || '',
      } as CompanyTheme);
      
      toast.success('Tema actualizado con éxito');
      return updatedTheme;
    } catch (error) {
      console.error('Error al actualizar el tema:', error);
      toast.error('Error al actualizar el tema');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return {
    form,
    isSubmitting,
    onSubmit: form.handleSubmit(onSubmit)
  };
}
